'use client'

import { useEffect, useRef } from 'react'
import styles from './fade-in-slide-up.module.css'

interface FadeInSlideUpProps {
  children: React.ReactNode
  delay?: number
  duration?: number
  className?: string
}

export function FadeInSlideUp({ children, delay = 0.3, duration = 0.5, className = '' }: FadeInSlideUpProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Add visible class when element enters viewport
          element.style.transitionDelay = `${delay}s`
          element.style.transitionDuration = `${duration}s`
          element.classList.add(styles.visible)
          // Disconnect after animation is triggered
          observer.disconnect()
        }
      },
      {
        threshold: 0.01,
        rootMargin: '0px 0px -10% 0px',
      },
    )

    observer.observe(element)

    // Fallback for very long content - ensure animation happens within reasonable time
    const timeoutId = setTimeout(() => {
      if (element && !element.classList.contains(styles.visible)) {
        element.style.transitionDelay = `${delay}s`
        element.style.transitionDuration = `${duration}s`
        element.classList.add(styles.visible)
        observer.disconnect()
      }
    }, 1000) // 1 second fallback

    return () => {
      observer.disconnect()
      clearTimeout(timeoutId)
    }
  }, [delay, duration])

  return (
    <div ref={ref} className={`${styles.fadeInSlideUp} ${className}`}>
      {children}
    </div>
  )
}
